import { useQuery } from "@tanstack/react-query";
import type {
  AdminWalletTopUpRequestListResponse,
  StoreSubscriptionPaymentReviewStatus,
} from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

type UseWalletTopUpRequestsQueryParams = {
  reviewStatus: StoreSubscriptionPaymentReviewStatus;
  limit?: number;
  offset?: number;
};

function buildPath(params: UseWalletTopUpRequestsQueryParams) {
  const query = new URLSearchParams();
  query.set("reviewStatus", params.reviewStatus);
  if (params.limit !== undefined) {
    query.set("limit", String(params.limit));
  }
  if (params.offset !== undefined) {
    query.set("offset", String(params.offset));
  }
  return `${endpoints.admin.storeWalletTopUpRequests}?${query.toString()}`;
}

/**
 * The cross-store wallet top-up review queue.
 *
 * `reviewStatus` is always sent rather than left to the server default: the
 * status is part of the page's URL, so the request and the address bar can
 * never disagree about which queue the admin is looking at.
 */
export function useWalletTopUpRequestsQuery(
  params: UseWalletTopUpRequestsQueryParams,
) {
  const query = useQuery({
    queryKey: queryKeys.admin.walletTopUpRequests({
      reviewStatus: params.reviewStatus,
      limit: params.limit,
      offset: params.offset,
    }),
    queryFn: () =>
      apiJsonClient<AdminWalletTopUpRequestListResponse>(buildPath(params)),
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
